import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Maximize2, Layers } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import GALLERY from '@/lib/creations';

const Galerie = () => {
  const [active, setActive] = useState(null);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  const data = GALLERY;
  
  const activeItem = active !== null ? data[active] : null;

  const mediaList = activeItem
    ? (activeItem.images && activeItem.images.length > 0 ? activeItem.images : [activeItem.src])
    : [];

  const prevImage = (e) => {
    if (e) e.stopPropagation(); 
    if (mediaList.length <= 1) return;
    
    setCurrentImgIndex((prev) => 
      prev === 0 ? mediaList.length - 1 : prev - 1
    );
  };

  const nextImage = (e) => {
    if (e) e.stopPropagation();
    if (mediaList.length <= 1) return;
    
    setCurrentImgIndex((prev) => 
      prev === mediaList.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <div data-testid="galerie-page" className="pt-32 md:pt-40">
      <section className="px-6 md:px-12 pb-16 md:pb-24">
        <div className="max-w-[1600px] mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-[#8B5A33] mb-6">
            Galerie
          </p>
          <Reveal>
            <h1 className="font-serif-el font-light text-[13vw] md:text-[8vw] leading-[0.9] tracking-tight">
              Nos <span className="italic text-[#8B5A33]">créations</span>
            </h1>
          </Reveal>
          <p className="max-w-lg mt-8 font-light text-[#5C564D] text-lg">
            Une sélection de meubles sur mesure et d'agencements réalisés à
            l'atelier. Cliquez sur une image pour l'agrandir.
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-28 md:pb-40">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
          {data.map((g, i) => {
            const isVideo = typeof g.src === 'string' && (
              g.src.toLowerCase().includes('.mp4') || 
              g.src.toLowerCase().includes('.mov') || 
              g.src.toLowerCase().includes('.webm')
            );

            // Vérifie si l'élément possède plusieurs images/médias
            const hasMultipleImages = g.images && g.images.length > 1;

            return (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <button
                  data-testid={`gallery-image-${i}`}
                  onClick={() => {
                    setActive(i);
                    setCurrentImgIndex(0);
                  }}
                  className="group block text-left w-full cursor-pointer"
                >
                  <div
                    className={`relative overflow-hidden rounded-sm ${
                      i % 5 === 0 ? "aspect-[4/5]" : i % 3 === 0 ? "aspect-square" : "aspect-[3/4]"
                    }`}
                  >
                    {isVideo ? (
                      <div className="w-full h-full relative overflow-hidden bg-black">
                        <video
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload="auto"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)]"
                        >
                          <source src={g.src} type="video/mp4" />
                          Votre navigateur ne supporte pas la lecture de vidéos.
                        </video>
                      </div>
                    ) : (
                      <img
                        src={g.src}
                        alt={g.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)]"
                      />
                    )}

                    {/* Indicateur de plusieurs images dans le coin supérieur droit */}
                    {hasMultipleImages && (
                      <div className="absolute top-3 right-3 z-10 bg-black/40 backdrop-blur-md text-[#F4F1EB] px-2.5 py-1.5 rounded-sm flex items-center gap-1.5 shadow-md">
                        <Layers size={15} strokeWidth={1.75} />
                        <span className="text-[11px] font-medium tracking-wide">
                          {g.images.length}
                        </span>
                      </div>
                    )}

                    {/* MODIFICATION ICI : Icône d'agrandissement visible en permanence (coin inférieur droit) */}
                    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-300 flex items-end justify-end p-3 pointer-events-none">
                      <div className="w-10 h-10 rounded-full bg-white/90 text-[#1C1A18] flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                        <Maximize2 size={18} strokeWidth={1.5} />
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col gap-1 border-b border-[#1C1A18]/10 pb-3 group-hover:border-[#8B5A33] transition-colors duration-300">
                    <span className="font-sans text-2xl">{g.title}</span>
                    <span className="text-xs text-[#8B5A33]">
                      {g.tag}
                    </span>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Lightbox inchangée */}
      <AnimatePresence>
        {active !== null && activeItem && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-[#1C1A18]/95 backdrop-blur-sm p-4 md:p-8"
            onClick={() => {
              setActive(null);
              setTimeout(() => setCurrentImgIndex(0), 300);
            }}
          >
            <div 
              className="relative w-full max-w-6xl h-full flex flex-col justify-center" 
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                data-testid="lightbox-close"
                onClick={() => setActive(null)} 
                className="absolute -top-2 right-0 md:top-0 md:-right-8 text-[#F4F1EB] hover:text-[#8B5A33] transition-colors z-[60]"
              >
                <X size={36} strokeWidth={1.5} />
              </button>

              <div className="relative flex-1 min-h-0 w-full flex items-center justify-center mt-6 md:mt-0">
                
                {mediaList.length > 1 && (
                  <>
                    <button 
                      onClick={prevImage} 
                      className="absolute left-0 md:-left-12 top-1/2 -translate-y-1/2 text-[#F4F1EB] hover:text-[#8B5A33] transition-colors z-50 cursor-pointer hidden md:block"
                    >
                      <ChevronLeft size={48} strokeWidth={1} />
                    </button>
                    <button 
                      onClick={nextImage} 
                      className="absolute right-0 md:-right-12 top-1/2 -translate-y-1/2 text-[#F4F1EB] hover:text-[#8B5A33] transition-colors z-50 cursor-pointer hidden md:block"
                    >
                      <ChevronRight size={48} strokeWidth={1} />
                    </button>
                  </>
                )}

                {(() => {
                  const currentMediaSrc = mediaList[currentImgIndex];
                  const isVideo = typeof currentMediaSrc === 'string' && (
                    currentMediaSrc.toLowerCase().includes('.mp4') || 
                    currentMediaSrc.toLowerCase().includes('.mov') || 
                    currentMediaSrc.toLowerCase().includes('.webm')
                  );
                  
                  const canSwipe = mediaList.length > 1;
                  
                  return (
                    <motion.div
                      drag={canSwipe ? "x" : false}
                      dragConstraints={{ left: 0, right: 0 }} 
                      dragElastic={0.8} 
                      onDragEnd={(e, { offset }) => {
                        if (!canSwipe) return; 
                        const swipeThreshold = 50; 
                        if (offset.x < -swipeThreshold) {
                          nextImage(); 
                        } else if (offset.x > swipeThreshold) {
                          prevImage(); 
                        }
                      }}
                      className={`w-full h-full flex items-center justify-center ${
                        canSwipe ? 'cursor-grab active:cursor-grabbing touch-pan-y' : ''
                      }`}
                    >
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={currentMediaSrc} 
                          initial={{ opacity: 0, scale: 0.98 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.98 }}
                          transition={{ duration: 0.2 }}
                          className="w-full h-full flex items-center justify-center pointer-events-none"
                        >
                          {isVideo ? (
                            <video 
                              src={currentMediaSrc} 
                              controls 
                              autoPlay 
                              playsInline
                              onPointerDownCapture={(e) => {
                                const rect = e.target.getBoundingClientRect();
                                const clickY = e.clientY - rect.top;
                                
                                if (rect.height - clickY < 70) {
                                  e.stopPropagation();
                                }
                              }}
                              className="max-w-full max-h-full object-contain shadow-2xl relative z-10 pointer-events-auto" 
                            />
                          ) : (
                            <img 
                              src={currentMediaSrc} 
                              alt={activeItem?.title || "Création"} 
                              className="max-w-full max-h-full object-contain shadow-2xl select-none pointer-events-auto" 
                            />
                          )}
                        </motion.div>
                      </AnimatePresence>
                    </motion.div>
                  );
                })()}
              </div>

              <div className="w-full shrink-0 pt-4 md:pt-6 pb-2 text-[#F4F1EB] z-10 overflow-y-auto max-h-[40vh] md:max-h-[30vh]">
                
                {mediaList.length > 1 && (
                  <div 
                    className="flex items-center gap-2 md:gap-3 mb-4 overflow-x-auto pb-2 scroll-smooth"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  >
                    {mediaList.map((src, idx) => {
                      const isThumbVideo = typeof src === 'string' && (
                        src.toLowerCase().includes('.mp4') || 
                        src.toLowerCase().includes('.mov') || 
                        src.toLowerCase().includes('.webm')
                      );
                      const isActiveThumb = currentImgIndex === idx;

                      return (
                        <button
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImgIndex(idx);
                          }}
                          className={`relative shrink-0 overflow-hidden rounded-sm transition-all duration-300 ${
                            isActiveThumb 
                              ? "w-12 h-12 md:w-16 md:h-16 border-2 border-[#8B5A33] opacity-100 scale-105" 
                              : "w-10 h-10 md:w-14 md:h-14 border border-[#F4F1EB]/20 opacity-50 hover:opacity-100"
                          }`}
                        >
                          {isThumbVideo ? (
                            <video 
                              src={src} 
                              className="w-full h-full object-cover pointer-events-none" 
                            />
                          ) : (
                            <img 
                              src={src} 
                              alt={`Vue ${idx + 1}`} 
                              className="w-full h-full object-cover pointer-events-none" 
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                <h3 className="font-serif-el text-2xl md:text-3xl drop-shadow-lg">
                  {activeItem?.title}
                </h3>
                
                {activeItem?.tag && (
                  <p className="text-xs md:text-sm mt-2 text-[#C49A76] tracking-normal drop-shadow-md whitespace-pre-line">
                    {activeItem?.tag}
                  </p>
                )}

                {mediaList.length > 1 && (
                  <p className="text-xs font-light tracking-normal mt-3 opacity-70">
                    {currentImgIndex + 1} / {mediaList.length}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Galerie;